import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useMutation, UseMutationResult, useQueryClient } from "react-query";
import { HTTPError } from "ky";
import { AuthenticatedContext } from "../crud/auth/context";
import { WorkspaceContext } from "./context";
import Cookies, { CookieAttributes } from "js-cookie";
import { JsonParam, QueryParamConfig, useQueryParam } from "use-query-params";
import { useLocalStorage } from "usehooks-ts";
import { isEqual } from "lodash";
import { Location, useLocation } from "react-router-dom";
import {
  ConfirmationErrorSchema,
  ConfirmationErrorSchemaFromJSONTyped,
  ResponseError,
} from "../api-client";

const isSafari = navigator.userAgent.includes("Safari");

const defaultCookieOptions: CookieAttributes = {
  // No matter what I do, if I set "secure" on Safari
  // the cookie isn't written. It _shouldn't_ be a big
  // deal since Lax or Strict will still protect against
  // CSRF.
  secure: import.meta.env.FE_COOKIE_SECURE === "true" && !isSafari,
  sameSite: "Strict",
};

export const useWorkspaceContextSlug = (): string => {
  const { slug } = useContext(WorkspaceContext);
  return slug;
};

export const useToken = (): string => {
  const { token } = useContext(AuthenticatedContext);
  return token;
};

export const useCookie = (
  key: string,
  defaultValue?: string,
  options?: CookieAttributes,
): [string | undefined, (val: string | undefined) => void] => {
  const [stateCookie, setStateCookie] = useState<string | undefined>(() =>
    Cookies.get(key) ? Cookies.get(key) : defaultValue,
  );
  const setCookie = useCallback(
    (val: string | undefined) => {
      setStateCookie(val);
      if (val === undefined) {
        Cookies.remove(key, options ? options : defaultCookieOptions);
      } else {
        Cookies.set(key, val, options ? options : defaultCookieOptions);
      }
    },
    [key, options],
  );
  return [stateCookie, setCookie];
};

// Hook from https://usehooks.com/useDebounce/
export const useDebounce = <T>(value: T, delay: number): T => {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay], // Only re-call effect if value or delay changes
  );
  return debouncedValue;
};

export const useInterval = (
  callback: () => void,
  delay: number | null,
): void => {
  const savedCallback = useRef(callback);

  // Remember the latest callback if it changes.
  useLayoutEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    // Don't schedule if no delay is specified.
    // Note: 0 is a valid value for delay.
    if (!delay && delay !== 0) {
      return;
    }

    const id = setInterval(() => savedCallback.current(), delay);

    return () => clearInterval(id);
  }, [delay]);
};

type JSONSerializablePrimitive = string | number | boolean | null | undefined;
interface JSONSerializableObject {}
interface JSONSerializableArray extends Array<JSONSerializable> {}
type JSONSerializableCustomObject = Record<any, unknown>;
type JSONSerializable =
  | JSONSerializablePrimitive
  | JSONSerializableArray
  | JSONSerializableObject
  | JSONSerializableCustomObject;

interface UsePersistentJsonOptions<T extends JSONSerializable> {
  key: string;
  defaultValue?: T;
  persistDefault?: boolean;
}

interface UsePersistentJsonOptionsWithQueryParams<T extends JSONSerializable>
  extends UsePersistentJsonOptions<T> {
  // avoid typing these because they don't have to persist `T`, just enough of `T` to match options
  // which means `type` + either `name` or `id`
  queryParam: any;
  setQueryParam: any;
}

type usePersistentJsonOverload = {
  <T extends JSONSerializable>(
    options: UsePersistentJsonOptionsWithQueryParams<T>,
  ): [T, (val: T | undefined, allowHistory?: boolean) => void];
  <T extends JSONSerializable>(
    options: UsePersistentJsonOptionsWithQueryParams<undefined>,
  ): [T | undefined, (val?: T | undefined, allowHistory?: boolean) => void];
};

const useLocationEffect = (callback: (location: Location) => void): void => {
  const location = useLocation();
  useEffect(() => {
    callback(location);
  }, [location, callback]);
};

// variable used to enforce only loading a key from the query parameters ONCE
const LoadedQueryParam: { [key: string]: boolean } = {};

export const usePersistentJson: usePersistentJsonOverload = <
  T extends JSONSerializable,
>({
  key,
  defaultValue,
  queryParam,
  setQueryParam,
  persistDefault = false,
}: UsePersistentJsonOptionsWithQueryParams<T>): [
  T | undefined,
  (val: T | undefined) => void,
] => {
  // Persist a json blob in local storage,
  // and as a query parameter in the URL.

  // Use a ref to keep track of the actual desired query param value
  const queryParamRef = useRef(queryParam);
  // Local storage hook
  const [localStorageValue, setLocalStorageValue] = useLocalStorage<
    T | undefined
  >(key, undefined);
  // State hook
  const [state, setState] = useState<T | undefined>(
    queryParam || localStorageValue || defaultValue,
  );
  const setFunction = useCallback(
    (val: T | undefined) => {
      if (isEqual(val, defaultValue) && !persistDefault) {
        // if this is default value (and we don't want to persist default), then clear what's in url and storage
        if (queryParam !== undefined) {
          setQueryParam(undefined, "replaceIn");
          queryParamRef.current = undefined;
        }
        if (localStorageValue !== undefined) {
          setLocalStorageValue(undefined);
        }
      } else {
        // otherwise, update url and storage if new value doesn't already match what's there
        if (!isEqual(queryParam, val)) {
          setQueryParam(val, "replaceIn");
        }
        if (!isEqual(localStorageValue, val)) {
          setLocalStorageValue(val);
        }
        if (!isEqual(queryParamRef.current, val)) {
          queryParamRef.current = val;
        }
      }
      setState(val);
    },
    [
      localStorageValue,
      queryParam,
      setLocalStorageValue,
      setQueryParam,
      defaultValue,
      persistDefault,
    ],
  );

  // This is what adds back the query param if you remove it or
  // navigate to a different URL that doesn't include query param.
  const keepQueryParamCorrect = useCallback(() => {
    if (LoadedQueryParam[key] === undefined) {
      // When the component mounts,
      // we should load from the query param as a priority,
      // then local storage takes precedence,
      // then the default value.
      if (queryParam) {
        setFunction(queryParam);
      } else if (localStorageValue) {
        setFunction(localStorageValue);
      } else {
        setFunction(defaultValue);
      }
      LoadedQueryParam[key] = true;
    } else {
      // If the query param changes, and something
      // besides us changed it (because our ref is incorrect),
      // set it back to what we expect.
      if (!isEqual(queryParamRef.current, queryParam)) {
        setQueryParam(queryParamRef.current, "replaceIn");
      }
    }
  }, [
    defaultValue,
    localStorageValue,
    queryParam,
    queryParamRef,
    setFunction,
    setQueryParam,
    key,
  ]);
  useLocationEffect(keepQueryParamCorrect);

  return [state, setFunction];
};

export const usePersistentJsonWithJsonParam = <T extends JSONSerializable>(
  options: UsePersistentJsonOptions<T>,
): [T, (val: T | undefined) => void] => {
  const [queryParam, setQueryParam] = useQueryParam(options.key, JsonParam);
  return usePersistentJson({ ...options, queryParam, setQueryParam });
};

type PersistentQueryParamOptions<T> = {
  key: string;
  default: T;
  paramType: QueryParamConfig<any>;
};

export const usePersistentQueryParam = <T = string>(
  options: PersistentQueryParamOptions<T>,
): [T, (val: T) => void] => {
  const [queryParam, setQueryParam] = useQueryParam<T>(
    options.key,
    options.paramType,
  );
  const queryParamRef = useRef(queryParam);
  const [localStorageValue, setLocalStorageValue] = useLocalStorage<
    T | undefined
  >(options.key, undefined);

  const setFunction = useCallback(
    (val: T) => {
      if (!isEqual(queryParam, val)) {
        setQueryParam(val, "replaceIn");
      }
      if (!isEqual(localStorageValue, val)) {
        setLocalStorageValue(val);
      }
      if (!isEqual(queryParamRef.current, val)) {
        queryParamRef.current = val;
      }
      setState(val);
    },
    [localStorageValue, queryParam, setLocalStorageValue, setQueryParam],
  );

  const [state, setState] = useState<T>(
    queryParam || localStorageValue || options.default,
  );

  useEffect(() => {
    setFunction(state);
  });
  return [state, setFunction];
};

export const useTimezone = (): [string, (val: string) => void] => {
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return useLocalStorage("tz", localTz);
};

export enum ChartColorScheme {
  PRETTY = "pretty",
  HIGH_CONTRAST = "high_contrast",
}

export const useChartColorScheme = (): [
  ChartColorScheme,
  (val: ChartColorScheme) => void,
] => {
  return useLocalStorage<ChartColorScheme>(
    "chartColorScheme",
    ChartColorScheme.PRETTY,
  );
};

// Confirmation mutation types and hook
interface ConfirmationState {
  isOpen: boolean;
  title: string;
  message: string;
  warningMessage?: string;
  confirmButtonText: string;
  isButtonDisabled?: boolean;
  originalArgs: any;
  mutationFn: (...args: any[]) => Promise<any>;
}

export interface UseConfirmationMutationOptions<TData, TError, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: TError, variables: TVariables) => void;
  invalidateQueries?: string[];
  getConfirmationConfig?: (
    error: ConfirmationErrorSchema,
    variables: TVariables,
  ) => {
    title: string;
    message?: string;
    warningMessage?: string;
    confirmButtonText: string;
  };
}

export interface UseConfirmationMutationResult<TData, TError, TVariables>
  extends Omit<
    UseMutationResult<TData, TError, TVariables>,
    "mutate" | "mutateAsync"
  > {
  mutate: (variables: TVariables) => void;
  mutateAsync: (variables: TVariables) => Promise<TData>;
  confirmationDialog: {
    isOpen: boolean;
    title: string;
    message: string;
    warningMessage?: string;
    confirmButtonText: string;
    isButtonDisabled?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    isLoading: boolean;
  } | null;
}

// Helper function to extract error information from both HTTPError and ResponseError
const extractErrorInfo = async (
  error: HTTPError | ResponseError,
): Promise<{
  status: number;
  getResponseJson: () => Promise<any>;
} | null> => {
  if (error instanceof HTTPError) {
    // ky HTTPError
    return {
      status: error.response.status,
      getResponseJson: async () => {
        if (error.response.bodyUsed) {
          throw new Error("Response body already consumed");
        }
        const clonedResponse = error.response.clone();
        return clonedResponse.json();
      },
    };
  } else if (error instanceof ResponseError) {
    // API client ResponseError
    return {
      status: error.response.status,
      getResponseJson: async () => {
        try {
          return await error.response.json();
        } catch {
          throw new Error("Failed to parse response JSON");
        }
      },
    };
  }
  return null;
};

export const useConfirmationMutation = <TData, TError, TVariables>({
  mutationFn,
  onSuccess,
  onError,
  invalidateQueries = [],
  getConfirmationConfig,
}: UseConfirmationMutationOptions<
  TData,
  TError,
  TVariables
>): UseConfirmationMutationResult<TData, TError, TVariables> => {
  const queryClient = useQueryClient();
  const [confirmationState, setConfirmationState] =
    useState<ConfirmationState | null>(null);

  const mutation = useMutation<TData, TError, TVariables>(mutationFn, {
    onSuccess: (data, variables) => {
      // Invalidate queries on success
      invalidateQueries.forEach((queryKey) => {
        queryClient.invalidateQueries(queryKey);
      });

      // Close confirmation dialog if open
      setConfirmationState(null);

      // Call custom onSuccess handler
      onSuccess?.(data, variables);
    },
    onError: async (error, variables) => {
      // Check if this is a 409 confirmation error from either HTTPError or ResponseError
      const errorInfo = await extractErrorInfo(
        error as HTTPError | ResponseError,
      );

      if (errorInfo && errorInfo.status === 409) {
        try {
          // Helper function to handle confirmation/retry scenarios
          const handleConfirmationFlow = (
            confirmationErrorData: ConfirmationErrorSchema,
          ) => {
            if (
              confirmationErrorData.actionRequired === "confirm" &&
              getConfirmationConfig
            ) {
              // Show confirmation dialog for user confirmation
              const config = getConfirmationConfig(
                confirmationErrorData,
                variables,
              );
              setConfirmationState({
                isOpen: true,
                message: config.message || confirmationErrorData.detail,
                ...config,
                isButtonDisabled: false,
                originalArgs: variables,
                mutationFn: (args: TVariables) => mutationFn(args),
              });
              return;
            }

            if (confirmationErrorData.actionRequired === "retry") {
              // Show retry dialog and automatically retry after 3-second delay
              if (getConfirmationConfig) {
                const config = getConfirmationConfig(
                  confirmationErrorData,
                  variables,
                );
                setConfirmationState({
                  isOpen: true,
                  title: config.title,
                  message: "Waiting for operation...",
                  warningMessage: config.warningMessage,
                  confirmButtonText: "Waiting...",
                  isButtonDisabled: true,
                  originalArgs: variables,
                  mutationFn: (args: TVariables) => mutationFn(args),
                });
              }
              setTimeout(() => {
                const retryArgs = {
                  ...variables,
                  confirm: true,
                };
                mutation.mutate(retryArgs);
              }, 3000);
              return;
            }
          };

          let confirmationError: ConfirmationErrorSchema;

          try {
            // Try to get the JSON response
            const errorJson = await errorInfo.getResponseJson();
            confirmationError = ConfirmationErrorSchemaFromJSONTyped(
              errorJson,
              false,
            );
          } catch (parseError) {
            // If we can't parse the response, create a fallback error
            if (getConfirmationConfig) {
              // Check if this is a retry scenario (user already confirmed)
              const isRetryScenario =
                variables &&
                typeof variables === "object" &&
                "confirm" in variables &&
                variables.confirm === true;

              confirmationError = {
                detail: isRetryScenario
                  ? "Operation in progress, please wait."
                  : "Confirmation required to proceed with this action.",
                actionRequired: isRetryScenario
                  ? ("retry" as const)
                  : ("confirm" as const),
              };
            } else {
              // Fall back to default confirmation dialog
              setConfirmationState({
                isOpen: true,
                title: "Confirmation Required",
                message: "Are you sure you want to proceed?",
                warningMessage: "This action may have consequences.",
                confirmButtonText: "Confirm",
                isButtonDisabled: false,
                originalArgs: variables,
                mutationFn: (args: TVariables) => mutationFn(args),
              });
              return;
            }
          }

          // Handle the confirmation scenario using the consolidated logic
          handleConfirmationFlow(confirmationError);
          return;
        } catch (parseError) {
          // Failed to parse confirmation error, fall through to normal error handling
        }
      }

      // For non-409 errors or errors without confirmation, call the error handler
      onError?.(error, variables);
    },
  });

  const handleConfirm = () => {
    if (confirmationState) {
      // Add confirmation flag to the original arguments
      const confirmedArgs = {
        ...confirmationState.originalArgs,
        confirm: true,
      };

      mutation.mutate(confirmedArgs);
    }
  };

  const handleCancel = () => {
    setConfirmationState(null);
  };

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
    data: mutation.data,
    reset: mutation.reset,
    isIdle: mutation.isIdle,
    status: mutation.status,
    context: mutation.context,
    failureCount: mutation.failureCount,
    isPaused: mutation.isPaused,
    variables: mutation.variables,
    confirmationDialog: confirmationState
      ? {
          ...confirmationState,
          onConfirm: handleConfirm,
          onCancel: handleCancel,
          isLoading: mutation.isLoading,
        }
      : null,
  };
};
